import { useCallback, useEffect } from 'react';
import { ParagraphInterface } from 'entities/interfaces';
import { createUserInput, createChatResponse } from 'utils/messageHelpers';
import { fetchChatGPTResponse } from 'api/chatGPT';
import { saveChatHistoryToDB } from 'utils/ChatHistoryHelpers';
import { userMsgId, gptMsgId, nextMsgId } from 'utils/idHelpers';
import { flushSync } from 'react-dom';

export const useChatNodes = ({
  nodesData,
  setNodesData,
  currentChatNode,
  setCurrentChatNode,
  setIsLoading,
  getNext,
  setTypingAllowed,
  setLastChatNodeOutputs,
  saveChatProgress,
  saveLastChatMessage,
  token,
  activeIdRef,
  activeId,
}: {
  nodesData: ParagraphInterface[];
  setNodesData: Function;
  currentChatNode: ParagraphInterface | null;
  setCurrentChatNode: Function;
  setIsLoading: Function;
  setTypingAllowed: Function;
  getNext: Function;
  setLastChatNodeOutputs: Function;
  saveChatProgress: Function;
  saveLastChatMessage: Function;
  token: string;
  activeIdRef: React.MutableRefObject<string | undefined>;
  activeId: string;
}) => {
  const formatBoldText = (text: string) => {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const appendUnique = <T extends { id: number }>(
    list: T[],
    item: T
  ): T[] => (list.some(n => n.id === item.id) ? list : [...list, item]);
  
  const handleUserInput = useCallback(
    async (userInput: string) => {
      if (!currentChatNode) return;
      const courseIdAtCall = activeIdRef.current;
      setTypingAllowed(false);
      setIsLoading(true);
      setTypingAllowed(false);
          const userMessageId = userMsgId(currentChatNode.id);
          const gptMessageId  = gptMsgId(currentChatNode.id);

      const userMessage = createUserInput(
        userMessageId,
        currentChatNode.section_id,
        userInput
      );
      setLastChatNodeOutputs(currentChatNode.outputs ?? null);

      setNodesData((prev: ParagraphInterface[])=> appendUnique(prev, userMessage));
      
      if (activeId && saveLastChatMessage) {
        saveLastChatMessage(activeId, userMessage);
      }
      
      try {
        const prompt = decodeURIComponent(currentChatNode.prompt || '');
        const gptResponse = await fetchChatGPTResponse(prompt, userInput);

        const formattedResponse = formatBoldText(gptResponse);

        const gptMessage = createChatResponse(
          gptMessageId,
          currentChatNode.section_id,
          formattedResponse
        );

        await saveChatHistoryToDB(token, currentChatNode.id, [
          { type: 'user', message: userInput },
          { type: 'gpt', message: formattedResponse },
        ]);

        await saveChatProgress(currentChatNode.id, userInput, formattedResponse);
        if (courseIdAtCall !== activeIdRef.current) {
          return;                   
        }
        setNodesData((prev: ParagraphInterface[]) => appendUnique(prev, gptMessage));

        if (activeId && saveLastChatMessage) {
          saveLastChatMessage(activeId, gptMessage);
        }

      } catch (err) {
        console.error(" GPT Verarbeitung fehlgeschlagen:", err);
      } finally {
        setIsLoading(false);
        setTypingAllowed(false);
        setCurrentChatNode(null);
      }
    },
    [currentChatNode, nodesData, activeId, saveLastChatMessage]
  );

  return { handleUserInput };
};